// Resolves a spec to its registered component (TASK-493, ports SixEase). Unknown
// name → FallbackCard. The renderer never throws on a bad name; per-cell render
// errors are caught upstream by ArtifactErrorBoundary in Canvas.
import { registry } from './registry';
import { FallbackCard } from './FallbackCard';
import { sanitizeWidgetFilters } from './filterContract';
import { WidgetProvenanceProvider } from './widgetProvenance';
import type { WidgetSpec } from './types';

export function WidgetRenderer({ spec }: { spec: WidgetSpec }) {
  if (spec.component === 'FallbackCard') {
    return <FallbackCard message={spec.props.message as string | undefined} />;
  }

  const Component = registry.get(spec.component);
  if (!Component) {
    return <FallbackCard component={spec.component} />;
  }

  // Filter trust boundary (TASK-495): props from a summon (LLM) or radar button may
  // carry unknown/out-of-range filter keys. Sanitize once here against the whitelist
  // and hand filterable widgets the clean subset as a dedicated `filters` prop.
  // Identity widgets ignore it; raw spec.props junk is harmless (adapters destructure).
  const filters = sanitizeWidgetFilters(spec.component, spec.props);
  // TASK-683: the spec is the only place that knows WHICH widget this is and for whom, and
  // the "Why this?" chrome deep inside it needs both to fetch the derivation chain.
  const clientId = spec.props?.clientId;
  return (
    <WidgetProvenanceProvider
      component={spec.component}
      clientId={typeof clientId === 'string' ? clientId : undefined}
    >
      <Component {...(spec.props as Record<string, unknown>)} filters={filters} />
    </WidgetProvenanceProvider>
  );
}
